import {
  ListBase,
  RecordContextProvider,
  useListContext,
  useLocaleState,
  useStore,
  useTranslate,
} from "ra-core";
import { useMemo } from "react";
import { ListPagination } from "@/components/admin/list-pagination";
import { ReferenceField } from "@/components/admin/reference-field";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { formatCurrency } from "../customFields/format";
import { ChoiceDot } from "../misc/ChoiceBadge";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Contact, CrmEvent } from "../types";
import { startOfDay } from "./calendarUtils";
import { useEventTypeLookup } from "./eventTypes";
import { formatEventDate } from "./formatEvent";

type EventPeriod = "upcoming" | "past";

const EVENTS_PER_PAGE = 50;

/** Upcoming events (soonest first) or past ones (latest first), as a table. */
export const EventList = ({
  onSelect,
}: {
  onSelect: (event: CrmEvent) => void;
}) => {
  const translate = useTranslate();
  const [period, setPeriod] = useStore<EventPeriod>(
    "events.listPeriod",
    "upcoming",
  );
  // Computed once per period, so the filter (and the query) stays stable.
  const filter = useMemo(() => {
    const today = startOfDay(new Date()).toISOString();
    return period === "upcoming"
      ? { "start_at@gte": today }
      : { "start_at@lt": today };
  }, [period]);

  return (
    <div className="flex flex-col gap-3">
      <Tabs
        value={period}
        onValueChange={(value) => setPeriod(value as EventPeriod)}
      >
        <TabsList>
          <TabsTrigger value="upcoming">
            {translate("resources.events.list.upcoming")}
          </TabsTrigger>
          <TabsTrigger value="past">
            {translate("resources.events.list.past")}
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <ListBase
        key={period}
        resource="events"
        filter={filter}
        sort={{
          field: "start_at",
          order: period === "upcoming" ? "ASC" : "DESC",
        }}
        perPage={EVENTS_PER_PAGE}
        disableSyncWithLocation
        storeKey={false}
      >
        <EventTable onSelect={onSelect} />
        <ListPagination />
      </ListBase>
    </div>
  );
};

const EventTable = ({ onSelect }: { onSelect: (event: CrmEvent) => void }) => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const { currency } = useConfigurationContext();
  const lookupType = useEventTypeLookup();
  const { data, isPending } = useListContext<CrmEvent>();
  if (isPending) return null;
  if (!data?.length) {
    return (
      <Card className="p-6 text-sm text-muted-foreground">
        {translate("resources.events.list.empty")}
      </Card>
    );
  }
  const label = (field: string) =>
    translate(`resources.events.fields.${field}`);

  return (
    <Card className="p-0 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{label("start_at")}</TableHead>
            <TableHead>{label("name")}</TableHead>
            <TableHead>{label("type")}</TableHead>
            <TableHead>{translate("resources.events.list.for")}</TableHead>
            <TableHead className="hidden xl:table-cell">
              {label("organizer_name")}
            </TableHead>
            <TableHead className="text-right">{label("price")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((event) => (
            <RecordContextProvider key={event.id} value={event}>
              <TableRow
                className="cursor-pointer"
                onClick={() => onSelect(event)}
              >
                <TableCell className="min-w-40 whitespace-normal">
                  {formatEventDate(event, locale)}
                </TableCell>
                <TableCell className="font-medium">{event.name}</TableCell>
                <TableCell>
                  {event.type ? (
                    <ChoiceDot choice={lookupType(event.type)} />
                  ) : null}
                </TableCell>
                <TableCell>
                  <EventTarget />
                </TableCell>
                <TableCell className="hidden xl:table-cell">
                  {event.organizer_name}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {event.price != null
                    ? formatCurrency(event.price, locale, currency)
                    : null}
                </TableCell>
              </TableRow>
            </RecordContextProvider>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

/** Company and contact the event is for. */
const EventTarget = () => (
  <span className="flex flex-col">
    <ReferenceField
      source="company_id"
      reference="companies"
      link={false}
      render={({ referenceRecord }) => referenceRecord?.name ?? null}
    />
    <ReferenceField<CrmEvent, Contact>
      source="contact_id"
      reference="contacts"
      link={false}
      render={({ referenceRecord }) =>
        referenceRecord ? (
          <span className="text-muted-foreground text-xs">
            {referenceRecord.first_name} {referenceRecord.last_name}
          </span>
        ) : null
      }
    />
  </span>
);
